// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $FullRegion,
} from 'common.data';
import type {
  $Translate,
} from 'common.language';

// Global Enums
import {
  CrewmemberAuthType,
  CrewmemberLanguage,
} from 'common.data';

// Global Components
import * as React from 'react';
import {
  NavLink,
} from 'react-router-dom';

// Helpers
import _ from 'lodash';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  scrollTop,
} from '@vroskus/library-helpers/dist/browser';
import {
  isRegionSeasonName,
} from 'common.helpers/dist/browser';
import {
  i18next,
} from 'common.language';
import config from '../../../config';

// Components
import CommonDialog from '../../dialog/CommonDialog';
import RegionsList from '../../common/RegionsList';
import SocialAuthButtons from '../../common/SocialAuthButtons';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $AuthenticateAction,
} from '../../../redux/modules/auth/actions';
import type {
  $RedirectAction,
  $SetRegionAction,
  $SetupUserAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate & $WithRouter;

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  authenticate: $AuthenticateAction;
  redirect: $RedirectAction;
  setRegion: $SetRegionAction;
  setupUser: $SetupUserAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

export type $State = {
  closeAuthDialogAction: () => void;
  showMobileMenu: boolean;
};

class HeaderView extends React.Component<$Props, $State> {
  wrapperRef: React.RefObject<HTMLDivElement>;

  handleClickOutsideBind: (event) => void;

  constructor(props: $Props) {
    super(props);

    this.state = {
      closeAuthDialogAction: () => {},
      showMobileMenu: false,
    };

    this.wrapperRef = React.createRef();
    this.handleClickOutsideBind = this.handleClickOutside.bind(this);
  }

  componentDidMount(): void {
    document.addEventListener(
      'mousedown',
      this.handleClickOutsideBind,
    );
  }

  componentWillUnmount(): void {
    document.removeEventListener(
      'mousedown',
      this.handleClickOutsideBind,
    );
  }

  handleClickOutside(event): void {
    const {
      showMobileMenu,
    } = this.state;

    if (showMobileMenu === true
      && this.wrapperRef
      && this.wrapperRef.current
      && !this.wrapperRef.current.contains(event.target)
    ) {
      this.setState({
        showMobileMenu: false,
      });
    }
  }

  async setRegion({
    RegionId,
  }): Promise<void> {
    const {
      main,
      setRegion,
    } = this.props;

    try {
      await setRegion({
        RegionId,
      });

      const region: $FullRegion | undefined = _.find(
        main.regions,
        {
          id: RegionId,
        },
      );

      if (window && region) {
        const regionUrl = `https://${region.Domain}.${config.DOMAIN}`;

        window.location.assign(regionUrl);
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async authenticate({
    token,
    type,
  }: {
    token: string;
    type: CrewmemberAuthType;
  }) {
    const {
      authenticate,
      redirect,
      setupUser,
    } = this.props;
    const {
      closeAuthDialogAction,
    } = this.state;

    try {
      const {
        value: user,
      } = await authenticate({
        language: i18next.language as CrewmemberLanguage,
        token,
        type,
      });

      if (user !== null) {
        await setupUser(user);
      }

      closeAuthDialogAction();

      redirect({
        pathname: '/profile',
      });
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  renderLogo() {
    const {
      t,
    } = this.props;

    return (
      <NavLink
        className={'brand'}
        end
        onClick={() => scrollTop()}
        to={'/'}
      >
        <img
          alt={t('Logo')}
          className={'max-h-12'}
          src={`${config.CDN_URL}/static/logo.png`}
        />
      </NavLink>
    );
  }

  renderRegattaChampionshipButton() {
    const {
      main,
      params,
      t,
    } = this.props;

    let regionRegattasPageLink = '/';
    let regionChampionshipsPageLink = '/championships';

    const {
      param1,
    } = params;

    if (param1 && isRegionSeasonName({
      regions: main.regions,
      value: param1,
    }) === true) {
      regionRegattasPageLink = `/${param1}`;
      regionChampionshipsPageLink = `/championships/${param1}`;
    }

    const getClass = (isActive: boolean): string => `btn btn_primary btn_outlined uppercase py-0.5 px-2 sm:px-3 ${isActive ? 'btn_active' : ''}`;

    return (
      <div className={'btn-group'}>
        <NavLink
          className={({
            isActive,
          }) => getClass(isActive)}
          end
          onClick={() => scrollTop()}
          title={t('Region regattas page')}
          to={regionRegattasPageLink}
        >
          <em className={'la la-flag text-2xl'} />
          <span className={'py-1 ml-2'}>
            {t('Regattas')}
          </span>
        </NavLink>
        <NavLink
          className={({
            isActive,
          }) => getClass(isActive)}
          end
          onClick={() => scrollTop()}
          title={t('Region championsips page')}
          to={regionChampionshipsPageLink}
        >
          <em className={'la la-trophy text-2xl'} />
          <span className={'hidden lg:block py-1 ml-2'}>
            {t('Championships')}
          </span>
          <span className={'block lg:hidden py-1 ml-2'}>
            {t('Champ.')}
          </span>
        </NavLink>
      </div>
    );
  }

  renderRegionsButton() {
    const {
      main,
      t,
    } = this.props;

    const activeRegion = _.find(
      main.regions,
      {
        id: main.RegionId,
      },
    ) as $FullRegion | undefined;

    return (activeRegion ? (
      <CommonDialog
        content={(
          <div className={'max-h-80 overflow-y-auto -mx-5 px-5 py-1'}>
            <RegionsList
              activeItemId={main.RegionId}
              data={main.regions}
              setItem={({
                RegionId,
              }) => this.setRegion({
                RegionId,
              })}
            />
          </div>
        )}
        title={t('Select region')}
      >
        <button
          className={'btn btn_primary btn_outlined flex justify-center'}
          title={t('Region')}
          type={'button'}
        >
          <img
            alt={activeRegion.Domain}
            className={'max-h-4 mr-2 border'}
            src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${activeRegion.Flag}.svg`}
          />
          <span>{activeRegion.Flag}</span>
        </button>
      </CommonDialog>
    ) : null);
  }

  renderProfileButton() {
    const {
      auth,
      t,
    } = this.props;

    const {
      user,
    } = auth;

    return (
      <NavLink
        className={({
          isActive,
        }) => `btn btn_primary btn_outlined uppercase ${isActive ? 'btn_active' : ''}`}
        end
        title={t('Profile page')}
        to={'/profile'}
      >
        {user !== null && user.RemoteImage !== null && (
          <img
            alt={'User'}
            className={'h-9 w-9 rounded-full -ml-5 -my-3 mr-2'}
            src={user.RemoteImage}
          />
        )}
        <span className={'whitespace-nowrap'}>
          {t('My data')}
        </span>
      </NavLink>
    );
  }

  renderLoginButton() {
    const {
      t,
    } = this.props;

    return (
      <CommonDialog
        closeAction={(closeAction) => this.setState({
          closeAuthDialogAction: closeAction,
        })}
        content={(
          <SocialAuthButtons
            onSuccessAction={(params) => this.authenticate(params)}
          />
        )}
        title={t('Sign in / Sign up')}
      >
        <button
          className={'btn btn_primary btn_outlined uppercase'}
          title={t('Profile page')}
          type={'button'}
        >
          <span className={'whitespace-nowrap'}>
            {t('My data')}
          </span>
        </button>
      </CommonDialog>
    );
  }

  renderMyDataButton() {
    const {
      auth,
    } = this.props;

    const {
      user,
    } = auth;

    return user !== null ? this.renderProfileButton() : this.renderLoginButton();
  }

  renderManageButton() {
    const {
      t,
    } = this.props;

    return (
      <a
        className={'btn btn_secondary btn_outlined uppercase'}
        href={config.ADMIN_URL}
        rel={'noreferrer'}
        target={'_blank'}
        title={t('Create and manage regattas')}
      >
        {t('Manage')}
      </a>
    );
  }

  render() {
    const {
      main,
      t,
    } = this.props;
    const {
      showMobileMenu,
    } = this.state;

    return (
      <nav
        className={'top-bar'}
        ref={this.wrapperRef}
      >
        <div className={'max-w-screen-2xl flex flex-wrap items-center justify-between w-full mx-auto'}>
          <div className={'mr-auto'}>
            {this.renderLogo()}
          </div>
          {main.RegionId !== null && (
            <>
              <div className={'md:hidden mr-5'}>
                {this.renderRegionsButton()}
              </div>
              <button
                aria-controls={'navbar-default'}
                aria-expanded={'false'}
                className={'inline-flex items-center p-2 w-10 h-10 justify-center text-sm text-blue-900 rounded-full md:hidden focus:outline-none focus:ring-1 focus:ring-blue-900'}
                data-collapse-toggle={'navbar-default'}
                onClick={() => this.setState({
                  showMobileMenu: !showMobileMenu,
                })}
                type={'button'}
              >
                <span className={'sr-only'}>
                  {t('Open menu')}
                </span>
                <svg
                  aria-hidden={'true'}
                  className={'w-5 h-5'}
                  fill={'none'}
                  viewBox={'0 0 17 14'}
                  xmlns={'http://www.w3.org/2000/svg'}
                >
                  <path
                    d={'M1 1h15M1 7h15M1 13h15'}
                    stroke={'currentColor'}
                    strokeLinecap={'round'}
                    strokeLinejoin={'round'}
                    strokeWidth={'2'}
                  />
                </svg>
              </button>
              <div
                className={'hidden w-full md:block md:w-auto'}
                id={'navbar-default'}
              >
                <ul className={'font-medium flex flex-row space-x-8'}>
                  <li>
                    {this.renderRegattaChampionshipButton()}
                  </li>
                  <li>
                    {this.renderRegionsButton()}
                  </li>
                  <li>
                    {this.renderMyDataButton()}
                  </li>
                  <li className={'hidden lg:block'}>
                    {this.renderManageButton()}
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>
        {showMobileMenu === true && (
          <ul
            className={'flex flex-row w-full fixed bg-white border-t shadow-md rounded-b-lg px-5 py-3 left-0 right-0 top-20'}
          >
            <li className={'mr-auto'}>
              {this.renderRegattaChampionshipButton()}
            </li>
            <li>
              {this.renderMyDataButton()}
            </li>
          </ul>
        )}
      </nav>
    );
  }
}

export default HeaderView;
